import {
  getIdTokenResult,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type Auth,
  type IdTokenResult,
  type UserCredential,
  type User,
} from 'firebase/auth';
import type { ReportsBackendGateway } from '../features/reports/reportsGateway';
import type { ReportsInvalidationGateway } from '../features/reports/reportsInvalidationGateway';
import { createFirebaseReportsBackendGateway } from '../features/reports/reportsBackendAdapter';
import { createFirestoreReportsInvalidationGateway } from '../features/reports/firestoreReportsInvalidationGateway';
import { createFirebaseUserAccountsBackendGateway } from '../features/userAccounts/usersBackendAdapter';
import type { UserAccountsBackendGateway } from '../features/userAccounts/userAccountsGateway';
import { createFirebaseRetentionGovernanceBackendGateway } from '../features/retentionGovernance/retentionGovernanceBackendAdapter';
import type { RetentionGovernanceBackendGateway } from '../features/retentionGovernance/retentionGovernanceGateway';

import type { FirebaseEnvironment } from './firebase/firebaseEnvironment';

type NextAuthUserCallback = (user: User | null) => void | Promise<void>;

export interface AuthSessionGateway {
  onIdTokenChanged: (callback: NextAuthUserCallback) => () => void;
  getIdTokenResult: (user: User, forceRefresh?: boolean) => Promise<IdTokenResult>;
  getCurrentUser: () => User | null;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<UserCredential>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export interface AppDependencies {
  authSession: AuthSessionGateway;
  reportsBackend: ReportsBackendGateway;
  reportsInvalidation: ReportsInvalidationGateway;
  retentionGovernanceBackend: RetentionGovernanceBackendGateway;
  userAccountsBackend: UserAccountsBackendGateway;
}

const createAuthSessionGateway = (auth: Auth): AuthSessionGateway => ({
  onIdTokenChanged: (callback: NextAuthUserCallback) =>
    onIdTokenChanged(auth, callback as (user: User | null) => void),
  getIdTokenResult: (user: User, forceRefresh = false) => getIdTokenResult(user, forceRefresh),
  getCurrentUser: () => auth.currentUser,
  signInWithEmailAndPassword: (email, password) =>
    signInWithEmailAndPassword(auth, email, password),
  sendPasswordResetEmail: (email) => sendPasswordResetEmail(auth, email),
  signOut: () => signOut(auth),
});

export const createAppDependencies = (environment: FirebaseEnvironment): AppDependencies => {
  const reportsBackend = createFirebaseReportsBackendGateway(environment.functions);

  return {
    authSession: createAuthSessionGateway(environment.auth),
    reportsBackend,
    reportsInvalidation: createFirestoreReportsInvalidationGateway(environment.db),
    retentionGovernanceBackend: createFirebaseRetentionGovernanceBackendGateway(
      environment.functions
    ),
    userAccountsBackend: createFirebaseUserAccountsBackendGateway(environment.functions),
  };
};
